import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { medicationService } from '../services/medicationService'
import { PatientMedication } from '../types/medication'
import './MedicationsCard.css'

interface MedicationsCardProps {
  patientId: string
}

export function MedicationsCard({ patientId }: MedicationsCardProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['patient-medications', patientId, 'active'],
    queryFn: () =>
      medicationService.getPatientMedications(patientId, {
        status: 'active',
      }),
  })

  const currentMedications = useMemo(
    () => (data?.items || []).filter((m) => m.status === 'active' && !m.isDoneInError),
    [data],
  )

  const formatMedicationDisplay = (med: PatientMedication): string => {
    const parts: string[] = [med.name]
    if (med.doseText) {
      parts.push(med.doseText)
    } else if (med.dose) {
      const doseStr = `${med.dose.value ?? ''} ${med.dose.unit ?? ''}`.trim()
      if (doseStr) parts.push(doseStr)
    }
    if (med.frequency?.text) {
      parts.push(med.frequency.text)
    } else if (med.frequency?.code) {
      parts.push(med.frequency.code)
    }
    return parts.join(' ')
  }

  if (isLoading) {
    return (
      <div className="medications-card-loading">
        <p>Loading medications...</p>
      </div>
    )
  }

  if (currentMedications.length === 0) {
    return (
      <div className="medications-card-empty">
        <p>No current medications</p>
      </div>
    )
  }

  return (
    <div className="medications-card">
      <div className="medications-card-label">Current Medications:</div>
      <ul className="medications-card-list">
        {currentMedications.map((med) => (
          <li key={med.medicationId} className="medications-card-item">
            <div className="medications-card-name">{med.name}</div>
            <div className="medications-card-details">
              {formatMedicationDisplay(med)}
              {med.indication && (
                <span className="medications-card-indication" title={med.indication}>
                  {' '}• {med.indication}
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
